
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRoute, useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';

  import { Shipment } from '@/store/modules/Shipments/ShipmentsModule';
  import lgZoom from 'lightgallery/plugins/zoom';
  import lgVideo from 'lightgallery/plugins/video';

  export default defineComponent({
    name: 'shipments-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    data: () => ({
      plugins: [lgZoom, lgVideo],
    }),
    async setup() {
      const { t, te } = useI18n();
      const { can } = useAbility();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const loading = ref(false);
      const editData = ref<number>(0);
      const selectedOffice = ref<string>('');
      const selectStatus = ref<string>('');
      const videoInput = ref('');

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('shipmentUpdating'), [
          translate('packages'),
        ]);
        reinitializeComponents();
      });

      // watch(
      //   () => selectedOrganizer.value,
      //   async () => {
      //     selectedLocation.value = 0;
      //     await store.dispatch(
      //       Actions.GET_ORGANIZER_LOCATIONS,
      //       selectedOrganizer.value
      //     );
      //   }
      // );

      const shipmentsSchema = Yup.object().shape({
        officeId: Yup.string()
          .notOneOf([''], () => translate('OFFICE_IS_REQUIRED_FIELD'))
          .required(() => translate('OFFICE_IS_REQUIRED_FIELD'))
          .label('Office'),
        statusId: Yup.string()
          .notOneOf([''], () => translate('STATUS_IS_REQUIRED_FIELD'))
          .required(() => translate('STATUS_IS_REQUIRED_FIELD'))
          .label('Status'),
      });

      const { data } = await store.dispatch(
        Actions.GET_SHIPMENT,
        route.params.id
      );

      const shipment = ref<Shipment>(data.data);

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        await store.dispatch(Actions.UPDATE_SHIPMENT, {
          data: values,
          id: shipment.value.id,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_SHIPMENT'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            submitButton.value?.removeAttribute('data-kt-indicator');
            // eslint-disable-next-line
            submitButton.value!.disabled = false;
          });
        }
      };

      const refreshPackages = async () => {
        await store.dispatch(
          Actions.GET_SHIPMENT_PROPERTIES,
          shipment.value.id
        );
      };

      await refreshPackages();
      const showPackagesEditModal = (val) => {
        editData.value = val;
      };

      await store.dispatch(Actions.GET_SHIPMENT_GW_OFFICES);
      await store.dispatch(Actions.GET_ALL_SHIPMENT_STATUSES, 'gw');

      return {
        shipment,
        editData,
        onSubmitUpdate,
        submitButton,
        translate,
        shipmentsSchema,
        goBack,
        can,
        loading,
        offices: computed(() => store.getters.gwOfficesList),
        shipmentStatuses: computed(() => store.getters.shipmentStatusesList),
        selectedOffice,
        selectStatus,
        videoInput,
        refreshPackages,
        showPackagesEditModal,
      };
    },
  });
